/**
 * 판정 기록 조회 API 서비스 (§6.3.9 계약, 백엔드 정본).
 *
 * @spec:2026-09-29-처방검토-판정기록-조회화면
 *
 * 각 메서드는 공통 봉투 `{ error, data?, message? }`를 언랩하지 않고 그대로 반환한다
 * (성공 판정 error===0은 훅에서 한 곳에서 — doseRules 관례). 실패는 훅이 DoseApiError로 던진다.
 * 요청별 timeout: 조회 30초(전역 10초로는 예산을 조금 넘는 응답이 timeout, D-12)·CSV 120초(D-6).
 * grades는 쉼표 문자열 1개(axios 기본 배열 직렬화 grades[]= 금지). 빈 값은 애초에 싣지 않는다.
 */
import axios from "api/axiosInstance";
import type {
  DoseReviewLogEnvelope,
  DoseReviewLogFilter,
  DoseReviewLogListQuery,
  DoseReviewLogListData,
  DoseReviewLogSummaryData,
  DoseReviewLogNeighbors,
  DoseReviewLogDetailData,
  DoseReviewLogExportResult,
  PutDoseLabelBody,
  PutDoseLabelData,
  DeleteDoseLabelData,
} from "./types";

const BASE = "/api/v1/admin/dose-review-logs";
const QUERY_TIMEOUT_MS = 30000;
const EXPORT_TIMEOUT_MS = 120000;

/**
 * 공통 필터 → axios params. 빈 값은 싣지 않고(trim), grades는 쉼표 문자열 1개,
 * source·labelState의 all(undefined)은 생략, ruleScope는 alert|applied로 늘 싣는다(L1240).
 */
export const toFilterParams = (f: DoseReviewLogFilter): Record<string, string> => {
  const p: Record<string, string> = { from: f.from, to: f.to };
  if (f.source) p.source = f.source;
  if (f.pharmacyUserId && f.pharmacyUserId.trim()) p.pharmacyUserId = f.pharmacyUserId.trim();
  if (f.alertOnly) p.alertOnly = "true";
  if (f.doseAlertOnly) p.doseAlertOnly = "true";
  if (f.grades && f.grades.length > 0) p.grades = f.grades.join(",");
  if (f.ruleId && f.ruleId.trim()) p.ruleId = f.ruleId.trim();
  p.ruleScope = f.ruleScope ?? "alert";
  if (f.product && f.product.trim()) p.product = f.product.trim();
  if (f.labelState && f.labelState !== "all") p.labelState = f.labelState;
  return p;
};

/** jsdom 16(Jest 27)엔 Blob.prototype.text()가 없다 → FileReader로 읽는다(R9). */
export const readBlobText = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });

export const doseReviewLogsApi = {
  /** GET /api/v1/admin/dose-review-logs — 목록(정렬 id DESC). */
  list: async (
    query: DoseReviewLogListQuery
  ): Promise<DoseReviewLogEnvelope<DoseReviewLogListData>> => {
    const params = {
      ...toFilterParams(query),
      limit: String(query.limit),
      offset: String(query.offset),
    };
    const res = await axios.get<DoseReviewLogEnvelope<DoseReviewLogListData>>(BASE, {
      params,
      timeout: QUERY_TIMEOUT_MS,
    });
    return res.data;
  },

  /** GET /api/v1/admin/dose-review-logs/summary — 상단 요약. */
  summary: async (
    filter: DoseReviewLogFilter
  ): Promise<DoseReviewLogEnvelope<DoseReviewLogSummaryData>> => {
    const res = await axios.get<DoseReviewLogEnvelope<DoseReviewLogSummaryData>>(`${BASE}/summary`, {
      params: toFilterParams(filter),
      timeout: QUERY_TIMEOUT_MS,
    });
    return res.data;
  },

  /** GET /api/v1/admin/dose-review-logs/:id/neighbors — 이전/다음·순번(D-26). */
  neighbors: async (
    id: number,
    filter: DoseReviewLogFilter
  ): Promise<DoseReviewLogEnvelope<DoseReviewLogNeighbors>> => {
    const res = await axios.get<DoseReviewLogEnvelope<DoseReviewLogNeighbors>>(
      `${BASE}/${id}/neighbors`,
      { params: toFilterParams(filter), timeout: QUERY_TIMEOUT_MS }
    );
    return res.data;
  },

  /**
   * GET /api/v1/admin/dose-review-logs/:id — 상세. from·to는 둘 다 또는 둘 다 없음(서버 DTO).
   * 직접 진입(기간 없음)은 range 없이 부른다.
   */
  detail: async (
    id: number,
    range?: { from: string; to: string }
  ): Promise<DoseReviewLogEnvelope<DoseReviewLogDetailData>> => {
    const res = await axios.get<DoseReviewLogEnvelope<DoseReviewLogDetailData>>(`${BASE}/${id}`, {
      params: range ? { from: range.from, to: range.to } : {},
      timeout: QUERY_TIMEOUT_MS,
    });
    return res.data;
  },

  /**
   * GET /api/v1/admin/dose-review-logs/export — CSV. 성공이면 blob, 실패면 HTTP 200 +
   * application/json 봉투 → Content-Type으로 가른다(Blob을 FileReader로 읽어 파싱). timeout 120초.
   */
  exportCsv: async (filter: DoseReviewLogFilter): Promise<DoseReviewLogExportResult> => {
    const res = await axios.get(`${BASE}/export`, {
      params: toFilterParams(filter),
      responseType: "blob",
      timeout: EXPORT_TIMEOUT_MS,
    });
    const blob = res.data as Blob;
    const contentType = String(
      (res.headers && (res.headers["content-type"] || res.headers["Content-Type"])) ?? ""
    );
    if (contentType.includes("application/json")) {
      const text = await readBlobText(blob);
      let envelope: DoseReviewLogEnvelope;
      try {
        envelope = JSON.parse(text) as DoseReviewLogEnvelope;
      } catch {
        envelope = { error: -1, message: "응답을 해석할 수 없습니다", data: null };
      }
      return { kind: "error", envelope };
    }
    return { kind: "csv", blob };
  },

  /**
   * PUT /api/v1/admin/dose-review-logs/:id/items/:index/label — 정오 표기 저장(upsert).
   * 성공·오류 모두 봉투 그대로 반환(언랩은 뮤테이션 훅의 call이 — error≠0이면 DoseApiError).
   * 성공 HTTP 200(공통 래퍼 — 201 아님, §1.1.5 #2). timeout 30초(결정 F1 — 잠금 대기 응답 유실 방지).
   */
  putLabel: async (
    logId: number,
    index: number,
    body: PutDoseLabelBody
  ): Promise<DoseReviewLogEnvelope<PutDoseLabelData>> => {
    const res = await axios.put<DoseReviewLogEnvelope<PutDoseLabelData>>(
      `${BASE}/${logId}/items/${index}/label`,
      body,
      { timeout: QUERY_TIMEOUT_MS }
    );
    return res.data;
  },

  /**
   * DELETE /api/v1/admin/dose-review-logs/:id/items/:index/label — 표기 취소(D-18).
   * 본문 {expectedRevision}(1 이상)은 axios 1.x에서 config.data로만 실린다(두 번째 인자 객체는 본문이 빠진다).
   */
  deleteLabel: async (
    logId: number,
    index: number,
    expectedRevision: number
  ): Promise<DoseReviewLogEnvelope<DeleteDoseLabelData>> => {
    const res = await axios.delete<DoseReviewLogEnvelope<DeleteDoseLabelData>>(
      `${BASE}/${logId}/items/${index}/label`,
      { data: { expectedRevision }, timeout: QUERY_TIMEOUT_MS }
    );
    return res.data;
  },
};
